/**
 * AuthProvider - Provides authentication tokens for API clients
 * Abstracts the authentication logic from the API clients
 */

export class AuthProvider {
  constructor() {
    this.token = null;
    this.user = null;
    this._initialize();
  }

  /**
   * Initialize auth provider with current token
   */
  _initialize() {
    // Get token from localStorage (where Cognito auth context stores it)
    this.token = localStorage.getItem('token');

    // Get user data if available
    const userData = localStorage.getItem('localUserData');
    if (userData) {
      try {
        this.user = JSON.parse(userData);
      } catch (error) {
        console.warn('Failed to parse user data from localStorage:', error);
      }
    }
  }

  /**
   * Get current authentication token
   */
  getToken() {
    // Always get fresh token from localStorage in case it was updated
    this.token = localStorage.getItem('token');
    return this.token;
  }

  /**
   * Get current user information
   */
  getUser() {
    return this.user;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * Update token (called when user logs in)
   */
  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('token', token);
    } else {
      localStorage.removeItem('token');
    }
  }

  /**
   * Update user information
   */
  setUser(user) {
    this.user = user;
    if (user) {
      localStorage.setItem('localUserData', JSON.stringify(user));
    } else {
      localStorage.removeItem('localUserData');
    }
  }

  /**
   * Clear authentication (called when user logs out)
   */
  clear() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('token');
    localStorage.removeItem('localUserData');
    localStorage.removeItem('localAuthToken');
  }

  /**
   * Listen for authentication changes
   */
  onAuthChange(callback) {
    // Listen for storage changes to detect auth changes in other tabs
    const handleStorageChange = (event) => {
      if (event.key === 'token') {
        this.token = event.newValue;
        callback(this.isAuthenticated(), this.user);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Return cleanup function
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }

  /**
   * Get auth headers for API requests
   */
  getAuthHeaders() {
    const token = this.getToken();
    if (!token) {
      return {};
    }

    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Create FormData with auth token
   */
  createAuthenticatedFormData(data = {}) {
    const formData = new FormData();

    // Add all data fields
    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    // Note: We don't add token to FormData as it goes in headers
    return formData;
  }
}

// Export singleton instance
export const authProvider = new AuthProvider();
export default authProvider;